'use client'

import Image from 'next/image'
import {FC} from 'react'

import {ContactUsBanner} from '@/components/common/banner/ContactUsBanner'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {Page} from '@/components/Page'
import {PricingHeroBenefits} from '@/components/pricing/PricingHeroBenefits'
import {PricingTabs} from '@/components/pricing/PricingTabs'
import {TrustpilotCarousel} from '@/components/trustpilot/TrustpilotCarousel'
import {getPricingContent, pricingTabs} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {PricingTabType} from '@/utils/pricing'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

type PricingProps = {
  activeTab: PricingTabType
}

const Pricing: FC<PricingProps> = ({activeTab}) => {
  const {t} = useTranslation()
  const {header, hero, contactBanner} = getPricingContent()
  const {heroTitle} = pricingTabs[activeTab]
  return (
    <>
      <Page
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () =>
            analytics.track('Click on Start registration (Detailed pricelist - Header)'),
        }}
      >
        <Hero
          title={t(heroTitle)}
          subtitle={t(hero.subtitle)}
          backgroundColor={theme.palette.background.default}
          alignItems="center"
        >
          <div className="image">
            <Image
              src={hero.imageSrc}
              alt={hero.imageAlt}
              sizes={`${MOBILE_SCREENSIZE} 195px, ${TABLET_SCREENSIZE} 320px, 360px`}
              fill
              priority
              style={{objectFit: 'contain'}}
            />
          </div>
        </Hero>
        <Section backgroundColor={theme.palette.background.butter}>
          <PricingHeroBenefits />
        </Section>
        <Section>
          <PricingTabs activeTab={activeTab} />
        </Section>
        <Section hasPaddingTop>
          <TrustpilotCarousel isCentered />
        </Section>
        <Section hasPaddingTop hasPaddingBottom>
          <ContactUsBanner
            description={t(contactBanner.description)}
            trackedEventParameters={{position: 'Contact us banner - Pricing'}}
          />
        </Section>
      </Page>

      <style jsx>{`
        .image {
          position: relative;
          width: 360px;
          height: 300px;
          margin-left: ${theme.spacing[8]};
          margin-right: ${theme.spacing[4]};
        }

        @media ${TABLET_SCREENSIZE} {
          .image {
            width: 320px;
            height: 250px;
            margin-top: ${theme.spacing[6]};
            margin-left: 0;
            margin-right: 0;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 195px;
            height: 150px;
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}

export default Pricing
